import { createStore, Action, action, Thunk, thunk } from 'easy-peasy';
import axios from 'axios';
import { API } from 'app/routes';

// ----------------- Time Management Model -----------------
export interface TimeManagementModel {
  fetching: boolean;
  entries: any[];
  periodicChecks: any[]; 

  setFetching: Action<TimeManagementModel, boolean>;
  setEntries: Action<TimeManagementModel, any[]>;
  save: Thunk<TimeManagementModel, any, void, Promise<any>>;
  getAllTimeData: Thunk<
    TimeManagementModel,
    { month?: number; year?: number },
    void,
    Promise<any>
  >;
  getAllActivitiesData: Thunk<
    TimeManagementModel,
    { month?: number; year?: number },
    void,
    Promise<any>
  >;
  getTimeManagementById: Thunk<
    TimeManagementModel,
    string,
    void,
    Promise<any>
  >;
  delete: Thunk<
    TimeManagementModel,
    string,
    void,
    Promise<any>
  >;
  getActiveEntry: Thunk<
  TimeManagementModel,
  void,
  void,
  Promise<any>
>;
  pauseResumeTimer: Thunk<
  TimeManagementModel,
  { id: number; action: 'pause' | 'resume' },
  void,
  Promise<any>
>;
getDailySummary: Thunk<
  TimeManagementModel,
  { date: string },
  void,
  Promise<any>
>;
setPeriodicChecks: Action<TimeManagementModel, any[]>; // ADD THIS

getPeriodicChecks: Thunk<
TimeManagementModel,
{ machineId: number; inspectionType: number },
void,
Promise<any>
>;
  getAbsenceTypes: Thunk<TimeManagementModel, void, void, Promise<any>>;
  getAbsenceById: Thunk<TimeManagementModel, number, void, Promise<any>>;
  saveAbsence: Thunk<TimeManagementModel, Record<string, unknown>, void, Promise<any>>;
  deleteAbsence: Thunk<TimeManagementModel, number, void, Promise<any>>;
  employeeHasAbsenceOnDate: Thunk<
    TimeManagementModel,
    { employee_id: number; date: string },
    void,
    Promise<any>
  >;
}

export const timeManagementModel: TimeManagementModel = {
  fetching: false,
  entries: [],
  periodicChecks: [], // ADD THIS

  setFetching: action((state, payload) => {
    state.fetching = payload;
  }),

  setEntries: action((state, payload) => {
    state.entries = payload;
  }),
  save: thunk((actions, payload) => {
    actions.setFetching(true);
  
    return axios.post(API.timeManagement, payload).then(
      response => {
        return response.data;
      }
    ).catch(error => {
      throw error;
    }).finally(() => {
      actions.setFetching(false);
    });
  }),
  getAllTimeData: thunk((actions) => {
    actions.setFetching(true);
    return axios.get(API.displayTimeManagement)
      .then(data => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),
  getAllActivitiesData: thunk((actions) => {
    actions.setFetching(true);
    return axios.get(API.dispalyAllActivites)
      .then(data => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),
  getTimeManagementById: thunk((actions, payload) => {
    actions.setFetching(true);
    return axios.get(`${API.displayTimeManagementById}/${payload}`)
      .then(data => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),
  delete: thunk((actions, paylaod) => {
    actions.setFetching(true);
    return axios.delete(`${API.deleteTimeManagementById}/${paylaod}`)
      .then(data => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),


  getActiveEntry: thunk((actions) => {
    actions.setFetching(true);
  
    return axios.get(`${API.getActiveTimeEntry}`)
      .then(response => {
  
        if (response.data?.entries && Array.isArray(response.data.entries)) {
          return {
            data: response.data.entries,   
            total_minutes_all: response.data.total_minutes,
            has_active: response.data.has_active,
            standardWorkingHoursPerDay: response.data.standardWorkingHoursPerDay
          };
        }
        return {
          data: [],
          total_minutes_all: 0,
          has_active: false
        };
      })
      .catch(error => {
        throw error;
      })
      .finally(() => {
        actions.setFetching(false);
      });
  }),
  

  pauseResumeTimer: thunk((actions, payload: { id: number; action: 'pause' | 'resume' }) => {
    actions.setFetching(true);
    
    const url = `${API.pauseResumeTimeEntry}/${payload.id}`;
    const data = { action: payload.action };
    return axios.post(url, data)
      .then(response => {
        return response;
      })
      .catch(error => {
        if (typeof error === 'string' && (error.includes('resumed') || error.includes('paused'))) {
          return { 
            data: { 
              result: 'success', 
              message: error 
            },
            status: 200
          };
        }
        
        if (error.response?.data?.result === 'success') {
          return error.response;
        }
        
        throw error;
      })
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  getDailySummary: thunk((actions, payload) => {
    actions.setFetching(true);
    return axios.post(API.getDailySummary, payload)
      .then(data => data)
      .catch(error => {
        throw error;
      })
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  setPeriodicChecks: action((state, payload) => {
    state.periodicChecks = payload;
  }),

  getPeriodicChecks: thunk((actions, payload) => {
    actions.setFetching(true);
    
    return axios.get(API.periodicCheck, {
      params: {
        machineId: payload.machineId,
        inspectionType: payload.inspectionType
      }
    })
    .then(response => {      
      if (response.data && response.data.checks) {
        actions.setPeriodicChecks(response.data.checks);
      }
      
      return response.data;
    })
    .catch(error => {
      console.error('Error fetching periodic checks:', error);
      actions.setPeriodicChecks([]);
      throw error;
    })
    .finally(() => {
      actions.setFetching(false);
    });
  }),

  getAbsenceTypes: thunk((actions) => {
    actions.setFetching(true);
    return axios
      .get(API.getTimeManagementAbsences)
      .then((data) => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  getAbsenceById: thunk((actions, id) => {
    actions.setFetching(true);
    return axios
      .get(`${API.getTimeManageAbsenceDataById}/${id}`)
      .then((data) => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  saveAbsence: thunk((actions, payload) => {
    actions.setFetching(true);
    return axios
      .post(API.saveTimeManageAbsenceData, payload)
      .then((data) => data)
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  deleteAbsence: thunk((actions, id) => {
    actions.setFetching(true);
    return axios
      .delete(`${API.deleteTimeManageAbsenceData}/${id}`)
      .then((data) => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),

  employeeHasAbsenceOnDate: thunk((actions, payload) => {
    actions.setFetching(true);
    return axios
      .post(API.employeeHasAbsenceOnDate, payload)
      .then((data) => data)
      .finally(() => {
        actions.setFetching(false);
      });
  }),

};

export const store = createStore({
  time: timeManagementModel,
});